import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../../contexts/TenantContext';
import { productionApi } from '../../api/production.api';
import type { Zone, Crop, CropTask, Harvest } from '../../types/production.types';

const TASK_TYPE_LABELS: Record<string, string> = {
  SEEDING: 'Semis', TRANSPLANT: 'Repiquage', WATERING: 'Arrosage',
  WEEDING: 'Desherbage', HARVEST: 'Recolte', TREATMENT: 'Traitement',
  FERTILIZING: 'Fertilisation', PRUNING: 'Taille', MULCHING: 'Paillage',
  HILLING: 'Buttage', NETTING: 'Filet protection', STAKING: 'Tuteurage',
  THINNING: 'Eclaircissage', OTHER: 'Autre',
};

const TASK_TYPE_COLORS: Record<string, string> = {
  SEEDING: 'bg-blue-100 text-blue-800',
  TRANSPLANT: 'bg-violet-100 text-violet-800',
  HARVEST: 'bg-green-100 text-green-800',
  WATERING: 'bg-cyan-100 text-cyan-800',
  WEEDING: 'bg-rose-100 text-rose-800',
  TREATMENT: 'bg-red-100 text-red-800',
  FERTILIZING: 'bg-amber-100 text-amber-800',
  PRUNING: 'bg-orange-100 text-orange-800',
  MULCHING: 'bg-lime-100 text-lime-800',
  HILLING: 'bg-stone-200 text-stone-800',
  NETTING: 'bg-indigo-100 text-indigo-800',
  STAKING: 'bg-teal-100 text-teal-800',
  THINNING: 'bg-sky-100 text-sky-800',
  OTHER: 'bg-gray-100 text-gray-700',
};

export default function ProductionDashboard() {
  const { t } = useTranslation();
  const { tenant, loading: tenantLoading } = useTenant();

  const [zones, setZones] = useState<Zone[]>([]);
  const [activeCrops, setActiveCrops] = useState<Crop[]>([]);
  const [weekTasks, setWeekTasks] = useState<CropTask[]>([]);
  const [recentHarvests, setRecentHarvests] = useState<Harvest[]>([]);
  const [loading, setLoading] = useState(true);
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);
  const [completionNote, setCompletionNote] = useState('');

  const currentWeek = getISOWeek(new Date());

  useEffect(() => {
    if (!tenantLoading && tenant) {
      loadDashboard();
    }
  }, [tenant, tenantLoading]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [zonesData, cropsData, tasksData, harvestsData] = await Promise.all([
        productionApi.listZones(),
        productionApi.listCrops({ status: 'GROWING' }),
        productionApi.getTasksByWeek(currentWeek),
        productionApi.listHarvests(),
      ]);
      setZones(zonesData);
      setActiveCrops(cropsData);
      setWeekTasks(tasksData);
      setRecentHarvests(harvestsData.slice(0, 10));
    } catch (err: any) {
      console.error('Error loading production dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      if (completionNote.trim()) {
        await productionApi.updateCropTask(taskId, { description: completionNote.trim() } as any);
      }
      await productionApi.completeCropTask(taskId);
      setCompletingTaskId(null);
      setCompletionNote('');
      loadDashboard();
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    }
  };

  const totalPlots = zones.reduce((sum, z) => sum + (z.plots?.length || 0), 0);
  const totalAreaM2 = zones.reduce((sum, z) => sum + Number(z.area_m2), 0);
  const pendingTasks = weekTasks.filter((t) => !t.completed_at);
  const completedTasks = weekTasks.filter((t) => !!t.completed_at);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('production.dashboard.title', 'Production')}
        </h1>
        <span className="text-sm text-gray-500">Semaine {currentWeek}</span>
      </div>

      {/* Cards resume */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label={t('production.dashboard.zones', 'Zones')}
          value={zones.length}
          sub={`${totalPlots} planches`}
          icon="M"
          highlight={false}
        />
        <StatCard
          label={t('production.dashboard.area', 'Surface')}
          value={`${(totalAreaM2 / 10000).toFixed(2)} ha`}
          sub={`${totalAreaM2} m2`}
          icon="S"
          highlight={false}
        />
        <StatCard
          label={t('production.dashboard.activeCrops', 'Cultures actives')}
          value={activeCrops.length}
          sub="en croissance"
          icon="C"
          highlight={false}
        />
        <StatCard
          label={t('production.dashboard.tasksThisWeek', 'Taches semaine')}
          value={pendingTasks.length}
          sub={`${completedTasks.length} terminees`}
          icon="T"
          highlight={pendingTasks.length > 0}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Taches de la semaine */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-3">
            {t('production.tasks.thisWeek', 'Taches cette semaine')} (S{currentWeek})
          </h2>
          {pendingTasks.length === 0 ? (
            <p className="text-gray-400 text-sm">Aucune tache en attente</p>
          ) : (
            <div className="space-y-3">
              {pendingTasks.map((task) => (
                <div key={task.id} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                  {/* Header: type badge + variety + plot */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TASK_TYPE_COLORS[task.type] || TASK_TYPE_COLORS.OTHER}`}>
                      {TASK_TYPE_LABELS[task.type] || task.type}
                    </span>
                    <span className="text-sm font-medium">{task.crop?.variety?.name}</span>
                    {task.crop?.variety?.species && (
                      <span className="text-xs text-gray-500">({task.crop.variety.species})</span>
                    )}
                    {task.crop?.plot && (
                      <span className="text-xs text-gray-400">{task.crop.plot.code}</span>
                    )}
                  </div>

                  {/* Notes (terrain instructions) */}
                  {task.notes && (
                    <div className="mt-2 text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 leading-relaxed whitespace-pre-line">
                      {task.notes}
                    </div>
                  )}

                  {/* Date + action */}
                  <div className="mt-2 flex items-center justify-between">
                    {task.planned_date && (
                      <span className="text-xs text-gray-400">
                        {new Date(task.planned_date).toLocaleDateString('fr-BE', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </span>
                    )}

                    {completingTaskId === task.id ? (
                      <div className="flex-1 ml-2 space-y-2">
                        <textarea
                          className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-green-500"
                          rows={1}
                          placeholder="Note (optionnel)..."
                          value={completionNote}
                          onChange={(e) => setCompletionNote(e.target.value)}
                          autoFocus
                        />
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleCompleteTask(task.id)}
                            className="bg-green-600 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-green-700 min-h-[36px]"
                          >
                            Valider
                          </button>
                          <button
                            onClick={() => { setCompletingTaskId(null); setCompletionNote(''); }}
                            className="px-3 py-1.5 rounded border border-gray-300 text-xs text-gray-600 hover:bg-gray-50 min-h-[36px]"
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setCompletingTaskId(task.id); setCompletionNote(''); }}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 active:bg-green-800 text-sm font-medium min-h-[44px]"
                      >
                        Fait
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Completed tasks this week */}
          {completedTasks.length > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-400 mb-2">{completedTasks.length} tache{completedTasks.length > 1 ? 's' : ''} terminee{completedTasks.length > 1 ? 's' : ''}</p>
              {completedTasks.map((task) => (
                <div key={task.id} className="p-2 bg-green-50 rounded mb-1 border border-green-100">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0" />
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${TASK_TYPE_COLORS[task.type] || TASK_TYPE_COLORS.OTHER}`}>
                      {TASK_TYPE_LABELS[task.type] || task.type}
                    </span>
                    <span className="text-sm text-green-800">{task.crop?.variety?.name}</span>
                    {task.crop?.plot && (
                      <span className="text-xs text-green-600">{task.crop.plot.code}</span>
                    )}
                  </div>
                  {task.description && (
                    <p className="text-xs text-green-700 mt-1 italic ml-5">Note : {task.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recoltes recentes */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-3">
            {t('production.harvests.title', 'Recoltes recentes')}
          </h2>
          {recentHarvests.length === 0 ? (
            <p className="text-gray-400 text-sm">Aucune recolte enregistree</p>
          ) : (
            <ul className="space-y-2">
              {recentHarvests.map((h) => (
                <li key={h.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div>
                    <span className="text-sm font-medium">
                      {h.crop?.variety?.species} - {h.crop?.variety?.name}
                    </span>
                    {h.crop?.plot && (
                      <span className="text-xs text-gray-500 ml-2">{h.crop.plot.code}</span>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-green-700">{h.quantity_kg} kg</span>
                    <br />
                    <span className="text-xs text-gray-400">
                      {new Date(h.harvest_date).toLocaleDateString('fr-BE')}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Navigation rapide */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <NavCard href="/production/zones" label="Parcellaire" />
        <NavCard href="/production/varieties" label="Varietes & Semences" />
        <NavCard href="/production/planning" label="Plan de culture" />
        <NavCard href="/production/zones" label="Analyses de sol" />
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon, highlight }: {
  label: string;
  value: string | number;
  sub: string;
  icon: string;
  highlight?: boolean;
}) {
  return (
    <div className={`bg-white rounded-lg shadow p-4 ${highlight ? 'ring-2 ring-green-500' : ''}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-lg font-bold text-gray-400">{icon}</span>
        <span className="text-2xl font-bold text-gray-900">{value}</span>
      </div>
      <p className="text-sm font-medium text-gray-700">{label}</p>
      <p className="text-xs text-gray-400">{sub}</p>
    </div>
  );
}

function NavCard({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      className="bg-white rounded-lg shadow p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors"
    >
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </a>
  );
}

function getISOWeek(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}
